import React from "react";
import { Link } from "react-router-dom";
import { m } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, FileText, Languages, LockKeyhole, Mic,
  NotebookPen, Search, ShieldCheck, Sparkles, UserRoundCheck,
} from "lucide-react";
import { Button } from "../../components/ui/primitives";
import VoiceAiraAudioLibrary from "./VoiceAiraAudioLibrary";
import VoiceAiraProductFilm from "./VoiceAiraProductFilm";
import {
  VOICE_AIRA_LANGUAGES, VOICE_AIRA_SCENARIOS, voiceAiraSceneSources,
} from "./voiceAiraScenarios";

const EASE = [0.22, 1, 0.36, 1];

const FLOW_STEPS = [
  {
    number: "01",
    eyebrow: "Capture",
    title: "Speak naturally. Keep eye contact.",
    body: "Start a consented note from any staff page, upload an audio file, or paste a transcript. The recording state stays visible while you move through the app.",
    detail: "Live record · upload audio · paste transcript",
    Icon: Mic,
  },
  {
    number: "02",
    eyebrow: "Structure",
    title: "Aira finds the signal in the conversation.",
    body: "The detected language stays visible. Fillers and false starts can be cleaned without changing names, numbers, dates or meaning, then Aira drafts a summary, decisions and action items.",
    detail: "English + 10 Indian languages",
    Icon: Languages,
  },
  {
    number: "03",
    eyebrow: "Review",
    title: "A person owns the final record.",
    body: "Correct the transcript, edit the draft and approve it before anything is shared. An agreed action can become a linked task in one click.",
    detail: "Draft first · approve before sharing",
    Icon: UserRoundCheck,
  },
];

const TRUST_POINTS = [
  {
    Icon: ShieldCheck,
    title: "Consent is required",
    body: "Capture cannot start until the organiser confirms that every participant has consented.",
  },
  {
    Icon: LockKeyhole,
    title: "Audio is discarded",
    body: "Audio is transcribed and discarded; only the reviewable text record and notes are retained.",
  },
  {
    Icon: UserRoundCheck,
    title: "Sharing stays deliberate",
    body: "Only approved summaries and agreed actions can be shared to eligible portals. The transcript stays private.",
  },
];

const reveal = (reduce, delay = 0) => ({
  initial: reduce ? false : { opacity: 0, transform: "translateY(18px)" },
  whileInView: reduce ? undefined : { opacity: 1, transform: "translateY(0px)" },
  viewport: { once: true, amount: 0.18 },
  transition: { duration: reduce ? 0 : 0.48, ease: EASE, delay: reduce ? 0 : delay },
});

function LanguageRibbon() {
  return (
    <div className="mt-7 border-y border-[#d8cfc4] py-4 dark:border-white/15">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="shrink-0 text-[10px] font-bold uppercase tracking-[0.15em] text-[#6d6257] dark:text-white/55">English + 10 Indian languages</p>
        <ul className="flex flex-wrap gap-1.5" aria-label="Voice Aira supported languages">
          {VOICE_AIRA_LANGUAGES.map((language) => {
            const highlighted = language.code === "te" || language.code === "od";
            return (
              <li
                key={language.code}
                lang={language.code === "od" ? "or" : language.code}
                className={`rounded-full px-2.5 py-1 text-[9.5px] font-semibold ${highlighted
                  ? "border border-ai/25 bg-ai/[0.09] text-ai-ink"
                  : "border border-[#ded6cc] bg-white/55 text-[#5f554b] dark:border-white/10 dark:bg-white/[0.05] dark:text-white/65"}`}
              >
                {language.native}{language.native === language.english ? "" : ` · ${language.english}`}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function VoiceAiraHero({
  mod,
  reduce,
  onDemo,
  onRegister,
  demoLabel,
  registerLabel,
}) {
  return (
    <section
      className="relative isolate overflow-hidden border-b border-[#ddd5cb] bg-[#eee8df] dark:border-white/10 dark:bg-[#120f14]"
      data-testid="voice-aira-hero"
      aria-labelledby="voice-aira-title"
    >
      <div className="pointer-events-none absolute -left-28 top-24 h-80 w-80 rounded-full bg-[#e8d9c5]/55 blur-3xl dark:bg-[#8c674c]/15" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-ai/[0.09] blur-3xl dark:bg-ai/15" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.22] [background-image:linear-gradient(rgba(124,58,237,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(29,78,216,0.08)_1px,transparent_1px)] [background-size:80px_80px] [mask-image:linear-gradient(to_bottom,black,transparent_62%)] dark:opacity-[0.1]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 pb-12 pt-10 sm:pb-16 sm:pt-14 lg:pb-20">
        <m.div {...reveal(reduce)} className="grid gap-7 lg:grid-cols-[minmax(0,1.12fr)_minmax(280px,0.58fr)] lg:items-end">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2.5">
              <Link
                to="/modules"
                className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[#ded6cc] bg-white/75 px-3.5 text-[12.5px] font-semibold text-[#62584e] transition-[background-color,border-color,color] hover:border-ai/25 hover:bg-white hover:text-[#231d18] focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/45 dark:border-white/15 dark:bg-white/[0.06] dark:text-white/70 dark:hover:border-ai/45 dark:hover:bg-white/[0.1] dark:hover:text-white"
                data-testid="module-back"
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All modules
              </Link>
              <span className="inline-flex min-h-11 items-center gap-2 rounded-full border border-ai/15 bg-ai/[0.07] px-3.5 text-[12px] font-bold text-[#5b21b6] dark:text-[#ddd6fe]">
                <Mic className="h-3.5 w-3.5" aria-hidden="true" /> Voice Aira · meeting notes
              </span>
            </div>

            <h1 id="voice-aira-title" className="mt-7 max-w-[15ch] font-heading text-[42px] font-extrabold leading-[0.99] tracking-[-0.045em] text-[#17130f] dark:text-white sm:text-[56px] lg:text-[68px]">
              Stay in the conversation. <span className="text-ai dark:text-[#a78bfa]">Aira drafts the record.</span>
            </h1>
            <p className="mt-6 max-w-[66ch] text-[17px] leading-[1.72] text-[#62584e] dark:text-white/70 sm:text-[18px]">
              Speak naturally in English or one of ten Indian languages, including Telugu and Odia. When your school's voice provider is configured, Voice Aira detects the language, keeps the meaning and turns consented school conversations into structured notes for a person to review.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                className="rounded-full px-7"
                data-testid="module-demo"
                onClick={onDemo}
              >
                {demoLabel} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-[#d8cfc4] bg-white/75 px-7 text-[#554b42] hover:bg-white dark:border-white/20 dark:bg-white/[0.06] dark:text-white/80 dark:hover:bg-white/[0.1]"
                data-testid="module-register"
                onClick={onRegister}
              >
                {registerLabel}
              </Button>
            </div>
          </div>

          <aside className="border-l border-[#ded6cc] pl-5 dark:border-white/15 lg:pb-2" aria-label="Voice Aira safeguards">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#685d52] dark:text-white/55">Built into every draft</p>
            <ul className="mt-4 space-y-3 text-[13px] font-semibold text-[#51483f] dark:text-white/75">
              {["Visible recording state", "Meaning kept during cleanup", "Drafted for human review"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-ai/[0.1] text-ai-ink">
                    <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </aside>
        </m.div>

        <m.div {...reveal(reduce, 0.04)}>
          <LanguageRibbon />
        </m.div>

        <m.div {...reveal(reduce, 0.06)} className="mt-10 sm:mt-12">
          <VoiceAiraProductFilm reduce={reduce} priority testId="voice-aira-module-film" />
        </m.div>
      </div>
    </section>
  );
}

function NotesQueryPanel({ reduce }) {
  return (
    <m.aside
      {...reveal(reduce, 0.05)}
      className="relative overflow-hidden rounded-[28px] border border-ai/15 bg-[#17111f] p-5 text-white shadow-[0_30px_70px_-44px_rgba(61,32,83,0.8)] sm:p-7"
      aria-label="Illustrative Ask Aira response"
    >
      <div className="absolute -right-12 -top-16 h-52 w-52 rounded-full bg-ai/25 blur-3xl" aria-hidden="true" />
      <p className="relative text-[11px] font-bold uppercase tracking-[0.16em] text-white/55">Ask your reviewed notes</p>
      <div className="relative mt-5 rounded-2xl border border-white/10 bg-white/[0.07] p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/[0.1] text-[#d8c8ff]">
            <Search className="h-4 w-4" aria-hidden="true" />
          </span>
          <p className="pt-1.5 text-[14px] font-medium leading-relaxed text-white">What did we agree for maths practice?</p>
        </div>
      </div>
      <div className="relative mt-3 rounded-2xl bg-[#ffffff] p-4 text-[#2b2431] sm:p-5">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.13em] text-[#6d28d9]">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Answer from your notes
        </div>
        <p className="mt-3 text-[13px] leading-[1.65] text-[#554b5e]">
          The family agreed to practise maths at home twice a week. The class teacher will share a progress note every Friday.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="rounded-full bg-[#f2edf9] px-2.5 py-1 text-[10px] font-semibold text-[#5b21b6]">[1] PTM · Class 6-B</span>
          <span className="rounded-full bg-[#eef4ff] px-2.5 py-1 text-[10px] font-semibold text-[#1d4ed8]">Reviewed note</span>
        </div>
      </div>
      <p className="relative mt-4 text-[11px] leading-relaxed text-white/55">Illustrative answer · access stays limited to notes this person is allowed to see</p>
    </m.aside>
  );
}

export function VoiceAiraStory({ reduce }) {
  return (
    <>
      <section className="relative overflow-hidden border-b border-[#d8d0c6] bg-[#f4efe8] dark:border-white/10 dark:bg-[#151119]" data-testid="voice-aira-flow" aria-labelledby="voice-aira-flow-title">
        <div className="pointer-events-none absolute inset-0 opacity-[0.2] [background-image:linear-gradient(rgba(124,58,237,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(29,78,216,0.08)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:radial-gradient(circle_at_75%_45%,black,transparent_72%)] dark:opacity-[0.08]" aria-hidden="true" />
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <m.div {...reveal(reduce)} className="grid gap-6 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-end">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ai-ink">From conversation to action</p>
              <h2 id="voice-aira-flow-title" className="mt-3 max-w-[12ch] font-heading text-[34px] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink sm:text-[46px]">
                One tap in. A reviewable note out.
              </h2>
            </div>
            <p className="max-w-2xl text-[15px] leading-[1.75] text-ink-2 lg:justify-self-end">
              Voice Aira is a notepad for school conversations, not a silent decision-maker. It helps the organiser capture what was said, shape a useful first draft and keep ownership of the final record.
            </p>
          </m.div>

          <div className="mt-10 grid gap-7 lg:grid-cols-[minmax(0,1.18fr)_minmax(310px,0.82fr)] lg:items-start">
            <div className="border-y border-line">
              {FLOW_STEPS.map((step, index) => {
                const Icon = step.Icon;
                return (
                  <m.article
                    key={step.number}
                    {...reveal(reduce, index * 0.045)}
                    className="grid gap-4 border-b border-line py-7 last:border-b-0 sm:grid-cols-[56px_minmax(0,1fr)] sm:gap-6"
                  >
                    <span className="font-heading text-[13px] font-bold tabular-nums text-ai-ink">{step.number}</span>
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-subtle">
                        <Icon className="h-4 w-4 text-ai-ink" aria-hidden="true" /> {step.eyebrow}
                      </div>
                      <h3 className="mt-2 font-heading text-[22px] font-bold leading-tight tracking-tight text-ink sm:text-[25px]">{step.title}</h3>
                      <p className="mt-3 max-w-2xl text-[13.5px] leading-[1.72] text-ink-2">{step.body}</p>
                      <p className="mt-4 text-[11px] font-semibold text-ai-ink">{step.detail}</p>
                    </div>
                  </m.article>
                );
              })}
            </div>
            <NotesQueryPanel reduce={reduce} />
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden border-b border-[#d8d0c6] bg-[#eee8df] dark:border-white/10 dark:bg-[#120f14]"
        data-testid="voice-aira-audio-examples"
        aria-label="Voice Aira multilingual audio examples"
      >
        <div className="pointer-events-none absolute -left-32 top-10 h-80 w-80 rounded-full bg-[#e8d6c0]/65 blur-3xl dark:bg-[#8c674c]/15" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-28 bottom-0 h-96 w-96 rounded-full bg-ai/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.2] [background-image:linear-gradient(rgba(124,58,237,0.09)_1px,transparent_1px),linear-gradient(90deg,rgba(29,78,216,0.07)_1px,transparent_1px)] [background-size:78px_78px] [mask-image:linear-gradient(to_bottom,black,transparent_76%)] dark:opacity-[0.08]" aria-hidden="true" />
        <m.div {...reveal(reduce)} className="relative mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <VoiceAiraAudioLibrary defaultLanguage="od" headingLevel={2} reduce={reduce} testId="voice-module-audio-library" />
        </m.div>
      </section>

      <section className="relative overflow-hidden border-b border-[#ccd5e6] bg-[#eaf0fa] dark:border-white/10 dark:bg-[#0e1728]" aria-labelledby="voice-aira-moments-title" data-testid="voice-aira-school-moments">
        <div className="pointer-events-none absolute -left-36 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-ai/10 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <m.div {...reveal(reduce)} className="relative grid gap-6 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:items-end">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ai-ink">Made for school conversations</p>
              <h2 id="voice-aira-moments-title" className="mt-3 max-w-[13ch] font-heading text-[32px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink sm:text-[42px]">
                Four school moments. One calm note canvas.
              </h2>
            </div>
            <p className="max-w-2xl text-[14.5px] leading-[1.75] text-ink-2 lg:justify-self-end">The same review boundary works across family conversations, admissions, staff decisions and a teacher's quick post-class thought. Each example stays visibly tied to its detected language and destination.</p>
          </m.div>

          <div className="relative mt-10 grid gap-4 md:grid-cols-2">
            {VOICE_AIRA_SCENARIOS.map((scenario, index) => {
              const sources = voiceAiraSceneSources(scenario.imageStem);
              return (
                <m.article
                  key={scenario.id}
                  {...reveal(reduce, index * 0.04)}
                  className="group grid overflow-hidden rounded-[24px] border border-white/60 bg-white/72 shadow-[0_24px_55px_-44px_rgba(25,45,82,0.75)] backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.06] sm:grid-cols-[150px_minmax(0,1fr)]"
                >
                  <div className="relative min-h-[180px] overflow-hidden sm:min-h-full">
                    <picture className="absolute inset-0 block h-full w-full">
                      <source type="image/avif" srcSet={`${sources.avif.split(", ")[0]}`} sizes="150px" />
                      <source type="image/webp" srcSet={`${sources.webp.split(", ")[0]}`} sizes="150px" />
                      <img src={sources.src.replace("w622", "w320")} alt={scenario.imageAlt} width="320" height="320" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.025]" />
                    </picture>
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_52%,rgba(16,12,24,0.58))]" aria-hidden="true" />
                    <span className="absolute bottom-3 left-3 rounded-full border border-white/30 bg-[#17111f]/72 px-2.5 py-1 text-[9px] font-bold text-white backdrop-blur-md">{scenario.nativeLanguage} · {scenario.language}</span>
                  </div>
                  <div className="p-5">
                    <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-ai-ink">{scenario.short}</p>
                    <h3 className="mt-1.5 font-heading text-[18px] font-bold leading-tight text-ink">{scenario.title}</h3>
                    <p className="mt-2 text-[12px] leading-[1.6] text-ink-2">{scenario.context}.</p>
                    <p className="mt-4 border-l-2 border-ai/35 pl-3 text-[11.5px] font-semibold leading-[1.55] text-ink">“{scenario.polished}”</p>
                    <p className="mt-3 text-[10px] font-semibold text-accent-active">{scenario.linkLabel}</p>
                  </div>
                </m.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-[#ddd3e9] bg-[#f2ecf8] dark:border-white/10 dark:bg-[#17111f]" aria-labelledby="voice-aira-trust-title" data-testid="voice-aira-trust">
        <div className="pointer-events-none absolute inset-0 opacity-[0.3] [background-image:radial-gradient(circle_at_12%_18%,rgba(124,58,237,0.14),transparent_28%),radial-gradient(circle_at_86%_78%,rgba(29,78,216,0.11),transparent_30%)] dark:opacity-[0.4]" aria-hidden="true" />
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <m.div {...reveal(reduce)} className="rounded-[30px] border border-ai/15 bg-ai/[0.035] p-6 sm:p-9 lg:p-11">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:items-end">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ai-ink">Privacy you can point to</p>
                <h2 id="voice-aira-trust-title" className="mt-3 font-heading text-[30px] font-extrabold leading-tight tracking-[-0.03em] text-ink sm:text-[39px]">The recorder is visible. Control stays human.</h2>
              </div>
              <p className="max-w-2xl text-[14px] leading-[1.75] text-ink-2 lg:justify-self-end">
                Voice Aira makes the recording state and review boundary explicit. Its controls support a school's privacy programme; the school remains responsible for notices, lawful use and configuration.
              </p>
            </div>

            <div className="mt-9 grid gap-0 border-y border-ai/15 lg:grid-cols-3">
              {TRUST_POINTS.map((point, index) => {
                const Icon = point.Icon;
                return (
                  <article key={point.title} className={`py-6 lg:px-7 ${index ? "border-t border-ai/15 lg:border-l lg:border-t-0" : ""}`}>
                    <Icon className="h-5 w-5 text-ai-ink" aria-hidden="true" />
                    <h3 className="mt-4 font-heading text-[17px] font-bold text-ink">{point.title}</h3>
                    <p className="mt-2 text-[12.5px] leading-[1.68] text-ink-2">{point.body}</p>
                  </article>
                );
              })}
            </div>
          </m.div>
        </div>
      </section>
    </>
  );
}

export function VoiceAiraInlineFacts() {
  return (
    <div className="grid gap-3 sm:grid-cols-3" aria-label="Voice Aira product facts">
      {[
        [Languages, "11 languages", "Including Telugu and Odia"],
        [NotebookPen, "Structured drafts", "Summary, decisions and action items"],
        [FileText, "Reviewable record", "Edit, approve, export or link to work"],
      ].map(([Icon, title, detail]) => (
        <div key={title} className="flex items-start gap-3 border-t border-line py-4">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ai-ink" aria-hidden="true" />
          <div>
            <p className="text-[12.5px] font-bold text-ink">{title}</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-ink-subtle">{detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function VoiceAiraModuleExperience(props) {
  return (
    <>
      <VoiceAiraHero {...props} />
      <VoiceAiraStory reduce={props.reduce} />
    </>
  );
}
